import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@primitives/form";
import { Switch } from "@/components/primitives/switch";
import { Label } from "@/components/primitives/label";
import { Slider } from "@/components/primitives/slider";
import { MemberRole } from "@/server/types/member";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import { GroupChatFormType } from "@/schemas/chat";

/**
 * Purpose: Manages thread settings for group chats (used in both creation and editing contexts)
 *
 * How it works:
 * 1. Provides a toggle to enable/disable threads in the group chat
 * 2. Provides a slider to configure the minimum member role required to post threads
 * 3. The slider uses numeric values (0-3) corresponding to role hierarchy
 * 4. Role labels are visually color-coded based on the selected permission level
 *
 * Key features:
 * - Switch control for enabling/disabling threads
 * - Visual role hierarchy display with color coding
 * - Slider control for role-based thread posting permissions
 * - Form state managed through react-hook-form integration
 */

interface ThreadSettingsFieldsProps {
  form: UseFormReturn<GroupChatFormType>;
}

export default function ThreadSettingsFields({
  form,
}: ThreadSettingsFieldsProps) {
  const { t } = useTranslation();

  return (
    <>
      <FormField
        control={form.control}
        name="allowThreads"
        render={({ field }) => (
          <FormItem className="mt-3 flex flex-row items-center justify-between gap-3">
            <div className="space-y-0.5">
              <FormLabel>{t("threads.allow")}</FormLabel>

              <FormDescription className="text-foreground/50">
                {t("threads.description")}
              </FormDescription>
            </div>
            <FormControl>
              <Switch
                checked={field.value || false}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="canPost"
        render={({ field }) => (
          <FormItem className={cn("mt-5 flex flex-col justify-between gap-5")}>
            <div className="space-y-0.5">
              <FormLabel>{t("threads.canPost")}</FormLabel>

              <FormDescription className="text-foreground/50">
                {t("threads.canPostDescription")}
              </FormDescription>
            </div>
            <FormControl>
              <>
                {/* Role labels with color-coded hierarchy */}
                <div className="mb-2 flex justify-between">
                  {[
                    {
                      value: MemberRole.OWNER,
                      label: t("threads.roles.ownerYou"),
                    },
                    {
                      value: MemberRole.ADMIN,
                      label: t("threads.roles.admins"),
                    },
                    {
                      value: MemberRole.MODERATOR,
                      label: t("threads.roles.moderators"),
                    },
                    {
                      value: MemberRole.MEMBER,
                      label: t("threads.roles.members"),
                    },
                  ].map((role) => (
                    <Label
                      key={role.value}
                      className={cn(
                        role.value <= (field.value || 0)
                          ? "text-secondary"
                          : "text-muted",
                      )}
                    >
                      {role.label}
                    </Label>
                  ))}
                </div>

                {/* Slider control for role selection */}
                <Slider
                  value={[field.value || 0]}
                  onValueChange={(e) => field.onChange(e[0])}
                  step={1}
                  min={0}
                  max={3}
                />
              </>
            </FormControl>
          </FormItem>
        )}
      />
    </>
  );
}
